import { useNavigate } from 'react-router'
import { ArrowRight, Building2 } from 'lucide-react'
import { useWorkspace } from '../../../../contexts/WorkspaceContext'
import { CreateWorkspaceDialog } from '../../../../components/CreateWorkspaceDialog'
import { Button } from '../../../../components/ui/button'

export default function WorkspacesPage() {
  const navigate = useNavigate()
  const { workspaces, activeWorkspace, memberRole, isLoading, error, selectWorkspace } = useWorkspace()

  const handleEnter = async (workspaceId: string) => {
    await selectWorkspace(workspaceId)
    navigate('/admin/usage')
  }

  if (memberRole && memberRole !== 'ADMIN') {
    return (
      <div className="p-6">
        <div className="rounded-base border-2 border-nb-border bg-secondary-bg p-6 shadow-shadow">
          <h2 className="font-heading text-lg">Workspace management is admin-only</h2>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl text-foreground">Workspaces</h1>
          <p className="mt-1 text-sm text-foreground/65">Choose a workspace to manage classes, teachers, and students.</p>
        </div>
        <CreateWorkspaceDialog />
      </div>

      {error ? (
        <div className="rounded-base border-2 border-nb-border bg-secondary-bg p-4 text-sm text-red-600 shadow-shadow">
          {error}
        </div>
      ) : null}

      {workspaces.length === 0 ? (
        <div className="rounded-base border-2 border-nb-border bg-secondary-bg p-6 shadow-shadow">
          <h2 className="font-heading text-lg">No workspaces found</h2>
          <p className="mt-2 text-sm text-foreground/65">Create a workspace to start managing RobolabX classes.</p>
          <div className="mt-4">
            <CreateWorkspaceDialog triggerLabel="Create first workspace" />
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {workspaces.map((workspace) => {
            const isActive = activeWorkspace?.id === workspace.id
            return (
              <section
                key={workspace.id}
                className="flex min-h-[170px] flex-col justify-between rounded-base border-2 border-nb-border bg-secondary-bg p-5 shadow-shadow"
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-base border-2 border-nb-border bg-main text-main-foreground">
                        <Building2 className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <h2 className="truncate font-heading text-lg">{workspace.name}</h2>
                        <p className="truncate text-xs text-foreground/55">{workspace.domain}</p>
                      </div>
                    </div>
                    {isActive ? (
                      <span className="rounded-base border-2 border-nb-border bg-background px-2 py-1 text-xs font-heading">
                        Active
                      </span>
                    ) : null}
                  </div>
                  {workspace.description ? (
                    <p className="mt-4 line-clamp-2 text-sm text-foreground/65">{workspace.description}</p>
                  ) : null}
                </div>
                <Button
                  className="mt-5 w-full"
                  disabled={isLoading}
                  onClick={() => void handleEnter(workspace.id)}
                >
                  Enter
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}
